import React from 'react';
import { Box, Text, usePrefersReducedMotion } from '@chakra-ui/react';

const TRACK_COLOR = '#BA3430';
const PROGRESS_COLOR = '#3DCD57';
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * 65.25;

interface ChargingSpeedStatusProps {
  percentage: number | null;
  label: string;
  testId?: string;
}

export function ChargingSpeedStatus({ percentage, label, testId = 'charging-speed-status' }: ChargingSpeedStatusProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const normalizedPercentage = percentage === null ? 0 : Math.min(100, Math.max(0, percentage));

  return (
    <Box
      position="relative"
      boxSize="154px"
      flexShrink={0}
      role="img"
      aria-label={percentage === null ? label : `${Math.round(normalizedPercentage)}% ${label}`}
      data-testid={testId}
    >
      <Box as="svg" viewBox="0 0 154 154" w="100%" h="100%" aria-hidden="true">
        <circle cx="77" cy="77" r="76.86" fill="#1D1F23" />
        <circle cx="77" cy="77" r="53" fill="#2A3039" />
        <circle cx="77" cy="77" r="65.25" fill="none" stroke={TRACK_COLOR} strokeWidth="8" />
        {percentage !== null && normalizedPercentage > 0 && (
          <circle
            cx="77"
            cy="77"
            r="65.25"
            fill="none"
            stroke={PROGRESS_COLOR}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${(normalizedPercentage / 100) * CIRCLE_CIRCUMFERENCE} ${CIRCLE_CIRCUMFERENCE}`}
            transform="translate(154 0) scale(-1 1) rotate(-90 77 77)"
            style={{ transition: prefersReducedMotion ? 'none' : 'stroke-dasharray 300ms ease' }}
          />
        )}
        <line x1="35.64" y1="77.14" x2="119.22" y2="77.08" stroke="#475260" strokeWidth="1.5" />
      </Box>
      <Box position="absolute" inset={0} pointerEvents="none" textAlign="center">
        <Text
          position="absolute"
          top="35px"
          w="100%"
          fontSize="36px"
          fontWeight="normal"
          lineHeight="40px"
          color={normalizedPercentage <= 50 ? TRACK_COLOR : PROGRESS_COLOR}
          data-testid={`${testId}-percentage`}
        >
          {percentage === null ? '—' : Math.round(normalizedPercentage)}
          {percentage !== null && (
            <Box as="span" fontSize="16px" lineHeight="normal">
              %
            </Box>
          )}
        </Text>
        <Text
          position="absolute"
          top="85px"
          left="35px"
          w="84px"
          fontSize="10px"
          fontWeight="normal"
          lineHeight="12px"
          color="#BDBEC0"
        >
          {label}
        </Text>
      </Box>
    </Box>
  );
}
